import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Select, Stat, Switch } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { LeadDrawer } from '@/components/LeadDrawer';
import { ServicePitch } from '@/components/ServicePitch';
import { getCampaignGoal, getCampaignInsights, getCampaigns, getLeads, qk, setLeadStatus, setLeadValue, type Campaign, type CampaignInsights } from '@/data/client';
import { useIsAdmin } from '@/auth/useIsAdmin';
import type { LeadStatus } from '@/data/status';

function ciAgo(iso?: string): string {
  if (!iso) return '';
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const h = Math.floor(mins / 60);
  return h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
}

/* Objective, positive-toned analysis of paid campaigns + the quality of the
   leads they bring (AI score from leadIQ). Descriptive only — no recommendations.
   Cached 24h; Refresh forces a fresh pass. Reuses the trafficIQ panel styles. */
function CampaignInsightsPanel({ range }: { range: string }) {
  const qc = useQueryClient();
  const key = [...qk.campaignInsights, range];
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: () => getCampaignInsights(range), staleTime: 5 * 60_000 });
  const refresh = useMutation({ mutationFn: () => getCampaignInsights(range, true), onSuccess: (d) => qc.setQueryData(key, d) });
  const d = data as CampaignInsights | undefined;

  if (isLoading) return <Card padding="lg" className="ti-panel"><p className="dash-muted">Analyzing campaigns…</p></Card>;
  if (!d || !d.configured || d.empty) return null;
  if (d.error) return (
    <Card padding="lg" className="ti-panel">
      <p className="dash-muted">{d.error.includes('ANTHROPIC') ? 'The AI key isn’t configured on the Worker.' : 'Couldn’t analyze campaigns right now — try Refresh.'}</p>
    </Card>
  );

  return (
    <Card padding="lg" className="ti-panel">
      <div className="ti-head">
        <div className="ti-title">
          <span className="ti-mark"><Icon name="sparkles" size={16} /></span>
          <div><h2>AI campaign analysis</h2><p className="dash-muted">Performance &amp; lead quality by campaign</p></div>
        </div>
        <Button variant="ghost" size="sm" iconLeft={<Icon name="refresh-cw" size={14} />} disabled={refresh.isPending} onClick={() => refresh.mutate()}>
          {refresh.isPending ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>

      {d.summary && <p className="ti-summary">{d.summary}</p>}

      {(d.campaigns?.length ?? 0) > 0 && (
        <div className="ti-section">
          <h3 className="ti-section__title">By campaign</h3>
          <ul className="ti-list">
            {d.campaigns!.map((c, i) => (
              <li key={i}>
                <span className="ti-list__name">{c.name}</span>
                <span className="ti-list__role">{c.role}</span>
                <span className="ti-list__stat">{c.stat}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {d.generatedAt && <p className="ti-stamp">Analysis from {ciAgo(d.generatedAt)} · updates daily · Refresh for the latest</p>}
    </Card>
  );
}

const RANGE_OPTS = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
];

/** A campaign is worth showing if it's active, or has any leads/ad metrics. */
function hasMeasurableData(c: Campaign): boolean {
  return c.leadCount > 0 || c.impressions > 0 || c.clicks > 0 || c.cost > 0 || c.conversions > 0;
}
function isVisibleByDefault(c: Campaign): boolean {
  return c.status === 'ENABLED' || hasMeasurableData(c);
}

const STATUS_VARIANT: Record<string, 'success' | 'neutral' | 'outline'> = {
  ENABLED: 'success',
  PAUSED: 'neutral',
  REMOVED: 'outline',
};
const LEAD_STATUS_VARIANT: Record<string, 'success' | 'accent' | 'warning' | 'neutral' | 'outline'> = {
  sold: 'success',
  proposal_sent: 'warning',
  worked: 'accent',
  lost: 'outline',
  unworked: 'neutral',
};
const LEAD_STATUS_LABEL: Record<string, string> = {
  sold: 'Sold',
  proposal_sent: 'Proposal',
  worked: 'Worked',
  lost: 'Lost',
  unworked: 'Unworked',
};

function money(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}
function money2(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
}
function ordinal(n: number): string {
  const v = n % 100;
  const suffix = v >= 11 && v <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] || 'th');
  return `${n}${suffix}`;
}
function fmtWhen(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '—';
}

function CampaignRow({ c, onLead }: { c: Campaign; onLead: (uid: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="cq-item--clickable" onClick={() => setOpen((o) => !o)}>
        <td>
          <div className="dash-cust">
            <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} />
            <span>{c.name}</span>
          </div>
        </td>
        <td>{c.hasAds ? <Badge variant={STATUS_VARIANT[c.status] ?? 'neutral'}>{c.status || '—'}</Badge> : <span className="dash-muted">no ads data</span>}</td>
        <td className="dash-val">{c.hasAds ? money2(c.cost) : '—'}</td>
        <td className="dash-val">{c.hasAds ? c.clicks.toLocaleString() : '—'}</td>
        <td className="dash-val">{c.hasAds ? c.conversions.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}</td>
        <td className="dash-val">{c.leadCount}</td>
        <td className="dash-val">{c.leadValue ? money(c.leadValue) : '—'}</td>
        <td className="dash-val">{c.soldCount || '—'}</td>
      </tr>
      {open && (
        <tr className="cq-who">
          <td colSpan={8}>
            {c.leads.length === 0 ? (
              <p className="dash-muted" style={{ padding: '8px 4px' }}>No attributed leads yet.</p>
            ) : (
              <table className="dash-table cq-who__table">
                <thead>
                  <tr><th>Lead</th><th>AI quality</th><th>Value</th><th>Status</th><th>Received</th></tr>
                </thead>
                <tbody>
                  {c.leads.map((l) => (
                    <tr key={l.uid} className="cq-item--clickable" onClick={() => onLead(l.uid)}>
                      <td><span className="cq-who__name">{l.name}</span></td>
                      <td className="dash-val">{l.score || '—'}</td>
                      <td className="dash-val">{l.value ? money2(l.value) : '—'}</td>
                      <td><Badge variant={LEAD_STATUS_VARIANT[l.status] ?? 'neutral'}>{LEAD_STATUS_LABEL[l.status] ?? l.status}</Badge></td>
                      <td className="dash-time">{fmtWhen(l.receivedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export function CampaignIQ() {
  const qc = useQueryClient();
  const [range, setRange] = useState('30d');
  const [showAll, setShowAll] = useState(false);
  const [activeUid, setActiveUid] = useState<string | null>(null);
  const { data, isLoading } = useQuery({ queryKey: ['campaigns', range], queryFn: () => getCampaigns(range) });
  // Full lead records (shared cache with leadIQ) so a clicked "who" row can open
  // the same contact drawer, matched by uid.
  const { data: leads = [] } = useQuery({ queryKey: qk.leads, queryFn: getLeads });
  const activeLead = activeUid ? leads.find((l) => l.uid === activeUid) ?? null : null;

  const setStatus = (uid: string, status: LeadStatus) => {
    void setLeadStatus(uid, status).then(() => {
      qc.invalidateQueries({ queryKey: qk.leads });
      qc.invalidateQueries({ queryKey: ['campaigns'] });
    });
  };
  const saveValue = (uid: string, value: number | null) => {
    void setLeadValue(uid, value).then(() => {
      qc.invalidateQueries({ queryKey: qk.leads });
      qc.invalidateQueries({ queryKey: ['campaigns'] });
    });
  };

  const campaigns = data?.campaigns ?? [];
  const visible = showAll ? campaigns : campaigns.filter(isVisibleByDefault);
  const hidden = campaigns.length - visible.length;
  const totals = data?.totals ?? { cost: 0, clicks: 0, impressions: 0, conversions: 0, conversionsValue: 0 };
  const paidLeads = data?.paidLeads ?? 0;
  // Cost per lead uses Google conversions (not attributed/tracked leads): many
  // clients have leads without call tracking, so Google counts more conversions
  // than leadIQ can attribute — dividing by conversions gives a truer CPL.
  const rangeShort = range === 'all' ? 'all-time' : range; // 7d | 30d | 90d | all-time
  const cpl = totals.conversions ? totals.cost / totals.conversions : null;

  // ROAS = revenue / ad spend. Prefer Google's tracked conversion value; when a
  // client isn't sending conversion value, fall back to the CRM lead value they
  // enter on attributed leads so the metric still populates from real data.
  const leadValueTotal = campaigns.reduce((s, c) => s + c.leadValue, 0);
  const usingGoogleValue = totals.conversionsValue > 0;
  const revenue = usingGoogleValue ? totals.conversionsValue : leadValueTotal;
  const roas = totals.cost && revenue ? revenue / totals.cost : null;

  // Admin-only monthly ad-spend goal + alert. The goal is per month (~30.4 days);
  // scale it to the selected window (e.g. ×~3 for 90d) and flag when actual spend
  // is >10% off that pace, in either direction. Never shown to client users.
  const isAdmin = useIsAdmin();
  const { data: goalData } = useQuery({ queryKey: ['campaignGoal'], queryFn: getCampaignGoal, enabled: isAdmin, staleTime: 5 * 60_000 });
  const goal = isAdmin ? goalData?.goal ?? null : null;
  const billingDay = isAdmin ? goalData?.billingDay ?? null : null;
  const rangeDays: Record<string, number | null> = { '7d': 7, '30d': 30, '90d': 90, all: null };
  const goalDays = rangeDays[range] ?? null;
  const expectedSpend = goal != null && goalDays != null ? (goal / 30.4) * goalDays : null;
  const spendDev = expectedSpend && expectedSpend > 0 ? (totals.cost - expectedSpend) / expectedSpend : null;
  const spendAlert: 'over' | 'under' | null = spendDev != null && Math.abs(spendDev) > 0.1 ? (spendDev > 0 ? 'over' : 'under') : null;

  const rangeSelect = (
    <label className="lead-field">
      <Icon name="calendar" size={15} />
      <Select options={RANGE_OPTS} value={range} onChange={(e) => setRange(e.target.value)} aria-label="Date range" />
    </label>
  );

  if (isLoading) {
    return (
      <div className="dash-stack">
        <div className="lead-toolbar"><div className="lead-toolbar__filters">{rangeSelect}</div></div>
        <div className="dash-empty"><span className="dash-empty__icon"><Icon name="target" size={28} /></span><p>Loading campaigns…</p></div>
      </div>
    );
  }

  if (data?.configured === false) {
    return (
      <ServicePitch
        icon="target"
        service="campaignIQ"
        headline="Know exactly what your ad spend is buying."
        benefits={[
          'Every dollar of Google Ads spend beside the leads it produced, campaign by campaign.',
          'Cost per lead and return on ad spend, refreshed every hour.',
          'Attributed leads open straight into leadIQ, so marking a job sold updates your return.',
          'A paid ads manager watching pacing against your monthly budget and adjusting.',
        ]}
        roles={['Account Manager', 'Paid Ads Manager', 'Brand / Strategy Manager']}
        ask="about Google Paid Ads and how campaignIQ can show ad spend ROI."
        adminHint={<>Admin: link the Google Ads customer id under this client&rsquo;s <strong>PPC</strong> tab, or tag paid leads with a <code>utm_campaign</code>.</>}
      />
    );
  }

  if (campaigns.length === 0) {
    return (
      <div className="dash-stack">
        <div className="lead-toolbar"><div className="lead-toolbar__filters">{rangeSelect}</div></div>
        <div className="dash-empty">
          <span className="dash-empty__icon"><Icon name="target" size={28} /></span>
          <h2>No campaigns in this range</h2>
          <p>Nothing ran in the {rangeShort} window. Try a longer range.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dash-stack">
      {isAdmin && goal != null && spendAlert && (
        <div className="ciq-spend-alert" role="alert">
          <Icon name="alert-circle" size={18} />
          <span>
            <strong>Check ad spend.</strong> {rangeShort} spend of {money(totals.cost)} is{' '}
            {Math.abs(spendDev! * 100).toFixed(0)}% {spendAlert === 'over' ? 'above' : 'below'} the goal pace
            {expectedSpend != null && <> ({money(expectedSpend)} expected for {rangeShort}, from a {money(goal)}/mo target)</>}.
          </span>
        </div>
      )}

      <div className="lead-toolbar">
        <div className="lead-toolbar__filters">{rangeSelect}</div>
        {isAdmin && (goal != null || billingDay != null) && (
          <div className="lead-toolbar__right">
            <span className={`ciq-goal ${spendAlert ? 'is-alert' : ''}`.trim()}>
              <Icon name="target" size={14} />
              {goal != null && <>Goal {money(goal)}/mo{expectedSpend != null && <> · {money(expectedSpend)} for {rangeShort}</>}</>}
              {billingDay != null && <>{goal != null ? ' · ' : ''}billing date ({ordinal(billingDay)} of each month)</>}
              <em>admin only</em>
            </span>
          </div>
        )}
      </div>

      <div className="dash-kpis dash-kpis--5">
        <Card padding="lg"><Stat label={`Ad spend (${rangeShort})`} value={totals.cost ? money(totals.cost) : '—'} sub="from Google Ads" /></Card>
        <Card padding="lg"><Stat label="Google Ads Conversions" value={totals.conversions ? totals.conversions.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—'} sub={`Google Ads (${rangeShort})`} info="Conversions Google Ads credits to a paid ad click, within Ads' attribution window. Paid-only — so it differs from GA4 Conversions and from Captured Leads in leadIQ." /></Card>
        <Card padding="lg"><Stat label="Confirmed Leads" value={paidLeads} sub="attributed in leadIQ" info="Actual Captured Leads attributed to paid campaigns (from leadIQ) — confirmed people, not Google's event count. This is why it's usually lower than Google Ads Conversions." /></Card>
        <Card padding="lg"><Stat label="Cost per lead" value={cpl != null ? money2(cpl) : '—'} sub="spend / Google Ads conversions" info="Ad spend ÷ Google Ads Conversions (not Captured Leads) — many leads lack call tracking, so Google Ads counts more conversions than leadIQ can attribute; dividing by conversions gives a truer per-conversion cost." /></Card>
        <Card padding="lg"><Stat label="ROAS" value={roas != null ? `${roas.toFixed(1)}×` : '—'} sub={usingGoogleValue ? 'conv. value / spend' : 'lead value / spend'} /></Card>
      </div>

      <CampaignInsightsPanel range={range} />

      <Card padding={false} className="dash-leads">
        <div className="dash-panelhead dash-panelhead--pad">
          <div>
            <h2>Campaigns</h2>
            <p>Ad metrics reconciled with the paid leads behind each campaign · click a row to see who</p>
          </div>
          {(hidden > 0 || showAll) && (
            <Switch label={`Show inactive & empty${hidden > 0 ? ` (${hidden})` : ''}`} checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
          )}
        </div>
        <table className="dash-table">
          <thead>
            <tr>
              <th>Campaign</th><th>Status</th><th>Cost</th><th>Clicks</th><th>Ads conv.</th><th>Confirmed leads</th><th>Lead value</th><th>Sold</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr><td colSpan={8} className="lead-none">No active campaigns with data. Toggle “Show inactive &amp; empty” to see all.</td></tr>
            ) : (
              visible.map((c) => <CampaignRow key={c.id} c={c} onLead={setActiveUid} />)
            )}
          </tbody>
        </table>
      </Card>

      <LeadDrawer lead={activeLead} onClose={() => setActiveUid(null)} onSetStatus={setStatus} onSaveValue={saveValue} />
    </div>
  );
}
